import { JSX, memo, useContext, useMemo, useState } from 'react';
import { FilterContext } from '../contexts/FilterContext';
import { QESButton, QESSelect } from '@app/shared/qes-standards';
import { QesIcon } from '@app/shared/icons/QESIcon';
import { QesIcons } from '@app/shared/icons/qesIcons';
import { FilterType, ProviderNetworkType } from '../../../enumerators';

import 'mapbox-gl/dist/mapbox-gl.css';
import { MultiselectNestedDropdown } from '@app/shared/checkbox-dropdown';
import { NestingParentDropdownItem } from '@app/shared/checkbox-dropdown/types';
import { CheckboxStateEnum } from '@app/shared/qes-standards/forms/Checkbox';
import { MapCustomControlProps } from '@app/shared/mapping/MapCustomControlProps';
import { createPortal } from 'react-dom';
import { useMapboxControl } from '@app/shared/mapping/useMapBoxControl';

interface FilterControlProps extends MapCustomControlProps {
  clearAllFilters: () => void;
  createMap: () => void;
}

const FilterControl = ({ clearAllFilters, createMap, ignoreInMapExport, mapRef, position }: FilterControlProps): JSX.Element => {

  const filterContext = useContext(FilterContext);

  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [filterIconStyle, setFilterIconStyle] = useState<string>('2px solid transparent');

  const sortedServiceAreaOptions: NestingParentDropdownItem[] = useMemo(() => {
    return filterContext.serviceArea.map(state => ({
      checkboxStatus: CheckboxStateEnum.Unchecked,
      children: state.subRegions.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)).map(subRegion => {
        return {
          display: (
            <span>
              {subRegion.displayName}
            </span>
          ),
          key: subRegion.regionSetId.toString(),
          name: subRegion.displayName,
        }
      }),
      key: state.regionCode,
      name: state.displayName,
    })).slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [filterContext.serviceArea]);

  const getSelectedServiceAreaName = useMemo(() => {
    return sortedServiceAreaOptions.flatMap(item => item.children).find(c => c.key === filterContext.selectedServiceArea)?.name ?? '';
  }, [filterContext.selectedServiceArea, sortedServiceAreaOptions]);

  const sortedProviders: NestingParentDropdownItem[] = useMemo(() => {
    return filterContext.providerSource.map((provider) => ({
      checkboxStatus: CheckboxStateEnum.Unchecked,
      children: provider.children?.map((child: { type: string; name: string }) => ({
        key: child.type,
        name: child.name,
      })) || [],
      key: provider.type || provider.name.toLowerCase().replace(/\s+/g, '-'), // Use a fallback key
      name: provider.name,
    }));
  }, [filterContext.providerSource]);

  const container = useMapboxControl({mapRef, position});
  if (!container) return null;

  return createPortal (
    <div {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
      <QESButton
        id="network-coverage-map__filter-button"
        className="network-coverage-map__filter-button"
        onMouseOut={() => {
          setFilterIconStyle("2px solid transparent");
        }}
        onMouseOver={() => {
          setFilterIconStyle("2px solid black");
        }}
        onClick={() => setShowFilters(!showFilters)}
        qesType="secondary"
        qesTestId="network-coverage-map__filter-button"
        style={{
          backgroundColor: "white",
          border: filterIconStyle,
        }}
      >
        <QesIcon
          className="network-coverage-map__filter-icon"
          id="network-coverage-map__filter-icon"
          iconName={QesIcons.FontAwesome.Filter}
          color="#828282"
          height="16"
          svgStyle={{ backgroundColor: 'transparent', padding: '0px' }}
          testId="network-coverage-map__filter-icon"
          width="16"
        />
      </QESButton>
      {showFilters &&
        <div className="col-12 col-md-auto network-coverage-map__filter-container mt-2">
          <div className='col-12 col-md-auto pt-2'>
            <MultiselectNestedDropdown
              customSelectInputText={getSelectedServiceAreaName}
              items={sortedServiceAreaOptions}
              containerClassName='w-100'
              hideSelectAll
              label="Service Area"
              hideParentCheckboxes
              onSelectionChanged={(selectedItems) => {
                const current = filterContext.serviceAreaRegionIdToRegionCodeMap.get(filterContext.selectedServiceArea);
                const selected = selectedItems.length > 0
                  ? filterContext.serviceAreaRegionIdToRegionCodeMap.get(selectedItems[selectedItems.length - 1])
                  : null;
                filterContext.replaceFilterUrl(FilterType.State, current?.stateCode, selected?.stateCode);
                filterContext.replaceFilterUrl(FilterType.County, current?.countyCode, selected?.countyCode);
                filterContext.setSelectedServiceArea(selectedItems[selectedItems.length - 1] ?? '');
              }}
              selectedItems={filterContext.selectedServiceArea ? [filterContext.selectedServiceArea] : []}
              testId="network-coverage-map__filter--service-area"
            />
          </div>
          <div className="col-12 col-md-auto pt-2">
            <QESSelect
              allowEmpty={true}
              isValid={filterContext.selectedSpecialty !== ''}
              label="Specialty"
              onChange={(e) => {
                filterContext.replaceFilterUrl(FilterType.Specialty, filterContext.selectedSpecialty, e.target.value);
                filterContext.setSelectedSpecialty(e.target.value);
              }}
              testId="network-coverage-map__filter--specialty"
              value={filterContext.selectedSpecialty}
            >
              {filterContext.sortedSpecialtyGroups.map((sp) => (
                <option
                  key={sp.code}
                  value={sp.code}
                >
                  {sp.name}
                </option>
              ))}
            </QESSelect>
          </div>
          <div className="col-12 col-md-auto pt-2">
            <MultiselectNestedDropdown
              items={sortedProviders}
              containerClassName="w-100"
              label="Provider Source"
              hideParentCheckboxes={false} 
              parentsAsValues
              keepParentOpen
              hideSelectAll
              onSelectionChanged={(selectedItems: string[]) => {
                filterContext.setSelectedProviderSources(
                  selectedItems.map(item => item as ProviderNetworkType)
                );
              }}
              selectedItems={filterContext.selectedProviderSources}
              testId="network-coverage-map__filter--provider-source"
            />

          </div>
          <span className="d-flex justify-content-end col-12 col-xl pb-2 network-coverage-map__filter-action-buttons">
            <QESButton
              qesType="secondary"
              disabled={filterContext.mapButtonsAreDisabled}
              onClick={createMap}
              qesTestId="network-coverage-map__generate"
            >
              Create
            </QESButton>
            <QESButton
              qesType="secondary"
              qesTestId="network-coverage-map__clear-all"
              disabled={filterContext.mapButtonsAreDisabled}
              onClick={clearAllFilters}
            >
              Clear All
            </QESButton>
          </span>
        </div>
      }
    </div>,
    container
    )
}

export default memo(FilterControl);